import { AppContext } from "components/App/AppContext";
import { SubmitButton } from "components/Button/SubmitButton";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const DEFAULT_BACK_BUTTON_TEXT = "Back";
const DEFAULT_CONTINUE_BUTTON_TEXT = "Continue";
const DEFAULT_SAVE_DRAFT_BUTTON_TEXT = "Save & Complete Later";
const DEFAULT_COMPLETE_APPLICATION_BUTTON_TEXT = "Complete Application";

const BACK_BUTTON_METADATA = JSON.stringify({ name: DEFAULT_BACK_BUTTON_TEXT });
const CONTINUE_BUTTON_METADATA = JSON.stringify({ name: DEFAULT_CONTINUE_BUTTON_TEXT });
const SAVE_DRAFT_BUTTON_METADATA = JSON.stringify({ name: DEFAULT_SAVE_DRAFT_BUTTON_TEXT });
const COMPLETE_APPLICATION_BUTTON_METADATA = JSON.stringify({ name: DEFAULT_COMPLETE_APPLICATION_BUTTON_TEXT });

export const ApplicationControls: React.FC<ApplicationControlsProps> = (props) => {
    const { settings } = useContext(AppContext);
    const ref = useRef(null);
    const [wrap, setWrap] = useState(false);

    useEffect(() => {
        if (!ref.current) return;

        const observer = new ResizeObserver(([{ target, contentRect }]) => {
            const getSpace = (el: Element | null) => {
                if (!el) return 0;
                const { paddingLeft, paddingRight, borderLeftWidth, borderRightWidth } = getComputedStyle(el);
                return parseFloat(paddingLeft) + parseFloat(paddingRight) + parseFloat(borderLeftWidth) + parseFloat(borderRightWidth);
            };

            const pageSpace = getSpace(target.parentElement);
            const rowGap = [...Array.from(target.children)].reduce(
                (sum, el) => sum + (el ? parseFloat(getComputedStyle(el).rowGap) || 0 : 0),
                0
            );

            const totalButtonsWidth = Array.from(target.querySelectorAll("button > span"))
                .slice(0, matchMedia("(width < 576px)").matches ? -1 : undefined)
                .reduce((sum, el) => {
                    const parent = getSpace(el.parentElement);
                    return sum + el.getBoundingClientRect().width + parent;
                }, 0);

            setWrap(contentRect.width < pageSpace + rowGap + totalButtonsWidth);
        });

        observer.observe(ref.current);

        return () => observer.disconnect();
    }, []);

    const wizardButtonBackText = (settings.wizardButtonBackText ?? "").trim() || DEFAULT_BACK_BUTTON_TEXT;
    const wizardButtonContinueText = (settings.wizardButtonContinueText ?? "").trim() || DEFAULT_CONTINUE_BUTTON_TEXT;
    const wizardButtonSaveDraftText = (settings.wizardButtonSaveDraftText ?? "").trim() || DEFAULT_SAVE_DRAFT_BUTTON_TEXT;
    const wizardButtonCompleteApplicationText =
        (settings.wizardButtonCompleteApplicationText ?? "").trim() || DEFAULT_COMPLETE_APPLICATION_BUTTON_TEXT;

    return (
        <div
            ref={ref}
            className={cn("application-page-controls border-top pt-4 mt-5 mb-4 d-flex gap-3 flex-column align-items-stretch", {
                "flex-sm-row-reverse": props.isFirstPage,
                "flex-md-row-reverse": !wrap,
            })}
        >
            <div
                className={cn("d-flex gap-3 flex-row-reverse align-items-stretch flex-grow-1", {
                    "flex-wrap": wrap,
                    "flex-sm-nowrap": wrap,
                })}
            >
                {!props.isLastPage && !props.isDisqualificationPage && (
                    <SubmitButton
                        className={cn("text-nowrap w-100", {
                            "ms-sm-auto w-sm-auto": props.isFirstPage,
                            "w-md-auto": !wrap,
                        })}
                        isSubmitting={props.isProcessing}
                        onClick={props.onContinue}
                        spinnerText={"Submitting..."}
                        data-metadata={CONTINUE_BUTTON_METADATA}
                    >
                        <span>
                            {wizardButtonContinueText} <FontAwesomeIcon icon={["fal", "arrow-right"]} />
                        </span>
                    </SubmitButton>
                )}
                {props.isLastPage && !props.isDisqualificationPage && (
                    <SubmitButton
                        className={cn("text-nowrap w-100", {
                            "ms-sm-auto w-sm-auto": props.isFirstPage,
                            "w-md-auto": !wrap,
                        })}
                        isSubmitting={props.isProcessing}
                        onClick={props.onContinue}
                        spinnerText={"Submitting..."}
                        data-metadata={COMPLETE_APPLICATION_BUTTON_METADATA}
                    >
                        <span>
                            {wizardButtonCompleteApplicationText} <FontAwesomeIcon icon={["fal", "check"]} />
                        </span>
                    </SubmitButton>
                )}
                {!props.isFirstPage && (
                    <Button
                        className={cn("text-nowrap w-100", {
                            "w-md-auto": !wrap,
                        })}
                        variant="secondary"
                        onClick={props.onBack}
                        data-metadata={BACK_BUTTON_METADATA}
                    >
                        <span>
                            <FontAwesomeIcon icon={["fal", "arrow-left"]} /> {wizardButtonBackText}
                        </span>
                    </Button>
                )}
            </div>
            {props.onSaveDraft && !props.isDisqualificationPage && (
                <SubmitButton
                    className="text-nowrap"
                    variant="outline-secondary"
                    isSubmitting={props.isProcessing}
                    onClick={props.onSaveDraft}
                    spinnerText={"Submitting..."}
                    disabled={props.isDisqualificationPage}
                    data-metadata={SAVE_DRAFT_BUTTON_METADATA}
                >
                    <span>
                        <FontAwesomeIcon icon={["fal", "arrow-down-to-square"]} /> {wizardButtonSaveDraftText}
                    </span>
                </SubmitButton>
            )}
        </div>
    );
};

interface ApplicationControlsProps {
    isFirstPage: boolean;
    isLastPage: boolean;
    onBack?: () => void;
    onContinue: () => void;
    onSaveDraft?: () => void;
    isProcessing?: boolean;
    isDisqualificationPage: boolean;
}
